import { useSelector } from "react-redux";
import { Flex, Input, Select } from "@mantine/core";
import { useEffect, useId } from "react";
import { initializeCollections } from "../redux/actions/collectionActions";
import { useAppDispatch } from "../redux/hooks";
import { CollectionResponse } from "../models/responses/CollectionResponse";
import { useSession } from "../contexts/SessionContext";

export default function CollectionDropdown(props: any) {
    const { setSelectedCollectionId, selectedCollectionId, compact } = props;
    const { currentSessionCollectionId } = useSession();
    const { collections, systemCollectionId } = useSelector((state: any) => state.collection);

    const dispatch = useAppDispatch();
    const selectId = useId();

    const collectionId = currentSessionCollectionId || selectedCollectionId || systemCollectionId;

    useEffect(() => {
        dispatch(initializeCollections());
    }, [dispatch]);

    useEffect(() => {
        if (collectionId) {
            setSelectedCollectionId(collectionId);
        }
    }, [collectionId, setSelectedCollectionId, currentSessionCollectionId]);

    const handleSelectionChange = (value: string | null) => {
        if (value) {
            const collection = collections.find((collection: CollectionResponse) => collection.Id === Number(value));
            if (collection) {
                setSelectedCollectionId(collection.Id);
            }
        }
    };

    const selectOptions = collections.map((collection: CollectionResponse) => ({
        value: String(collection.Id),
        label: collection.Name,
    }));

    if (compact) {
        return (
            <Select
                id={selectId}
                data={selectOptions}
                value={collectionId != null ? String(collectionId) : null}
                onChange={handleSelectionChange}
                placeholder="Collection..."
                disabled={currentSessionCollectionId != null}
                size="xs"
                styles={{
                    input: {
                        // Firefox leaves this Mantine Select input non-interactive
                        // without an explicit pointer-events value (works on Chrome
                        // regardless). The non-compact variant already sets this;
                        // the compact pill needs it too or the dropdown can't be
                        // opened on Firefox and stays stuck on the current value.
                        pointerEvents: 'auto',
                        border: '1px solid #e8e4dc',
                        borderRadius: '20px',
                        background: '#f5f2ec',
                        fontSize: '12px',
                        color: '#5a5652',
                        height: '28px',
                        minHeight: '28px',
                        lineHeight: '28px',
                        paddingLeft: '12px',
                        paddingRight: '28px',
                        fontFamily: "'Instrument Sans', sans-serif",
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'border-color 0.15s ease, background 0.15s ease',
                        '&:hover': {
                            borderColor: '#c8c3b8',
                            background: '#f0ece4',
                        },
                        '&:focus': {
                            borderColor: '#b0a89e',
                            boxShadow: 'none',
                        },
                        '&:disabled': {
                            opacity: 0.65,
                            cursor: 'default',
                        },
                    },
                    section: {
                        color: '#8a8278',
                        right: '8px',
                    },
                    dropdown: {
                        border: '0.5px solid #e4e0d8',
                        borderRadius: '10px',
                        boxShadow: '0 4px 16px rgba(28,28,26,0.08)',
                        background: '#ffffff',
                        padding: '4px',
                    },
                    option: {
                        borderRadius: '7px',
                        fontSize: '12.5px',
                        color: '#3a3630',
                        fontFamily: "'Instrument Sans', sans-serif",
                        '&[data-selected]': {
                            background: '#1c1c1a',
                            color: '#faf8f4',
                        },
                        '&[data-hovered]': {
                            background: '#f5f2ec',
                        },
                    },
                }}
            />
        );
    }

    return (
        <Flex gap="sm" className="collection-dropdown" wrap="wrap" align="center">
            <Input.Label htmlFor={selectId} required>
                Collection:
            </Input.Label>

            <Select
                id={selectId}
                data={selectOptions}
                value={String(collectionId)}
                onChange={handleSelectionChange}
                placeholder="Select a collection..."
                disabled={currentSessionCollectionId != null}
                styles={{
                    input: {
                        pointerEvents: 'auto',
                    }
                }}
                style={{ flex: 1 }}
            />
        </Flex>
    )
}
